import React, { useCallback, useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { useSearchParams } from 'react-router-dom';
import { Loader2, Mail, Plus, Send, ToggleLeft, ToggleRight, Trash2, UserCog, X } from 'lucide-react';
import { adminApi } from '../services/api';
import Candidatures from '../components/Candidatures';
import BaremeGrades from '../components/BaremeGrades';
import EtatVide from '../components/EtatVide';
import SupervisionDocuments from '../components/SupervisionDocuments';
import {
  annoncerErreur, annoncerSucces, confirmer, EtatErreur, EtatSansDonnees, Squelette,
} from '../etats';

/* Les profils d'intervenant, dans l'ordre ou le serveur les declare
   (`PROFILS_INTERVENANT`). Coach et mentor partagent le meme role technique :
   c'est le profil, pas le role, qui dit ce que la personne fait. */
const PROFILS = [
  { cle: 'coach', libelle: 'Coach' },
  { cle: 'mentor', libelle: 'Mentor' },
  { cle: 'formateur', libelle: 'Formateur' },
  { cle: 'expert', libelle: 'Expert métier' },
  { cle: 'benevole', libelle: 'Bénévole' },
];

/* Un compte cree avant l'existence du champ n'en porte aucun : c'est un coach,
   et le traiter comme tel evite une categorie « sans profil » qui n'aurait
   jamais existe dans les faits. */
const profilDe = (compte) => {
  // Un profil saisi autrement (« Coach », espace en trop) ne doit pas faire
  // disparaître la personne de toutes les pastilles.
  const p = String(compte.profil_intervenant || '').trim().toLowerCase();
  return PROFILS.some((x) => x.cle === p) ? p : 'coach';
};

function CoachModal({ onClose, onSave }) {
  const [form, setForm] = useState({
    profil: 'coach', username: '', email: '', first_name: '', last_name: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      await onSave(form);
      onClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        className="bg-[var(--bg-card)] rounded-carte w-full max-w-md p-8 shadow-carte"
      >
        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <h3 className="text-xl font-semibold text-[var(--text-bold)]">Nouvel intervenant</h3>
          <button onClick={onClose} className="act act-icone act-tertiaire">
            <X className="w-5 h-5 text-[var(--text-muted)]" />
          </button>
        </div>

        {error && (
          <div className="encart encart-alerte mb-4">
            {error}
          </div>
        )}

        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="block surtitre text-[var(--text-muted)] mb-1.5">
              Profil <span className="text-[var(--alerte)]">*</span>
            </label>
            <select
              value={form.profil}
              onChange={e => setForm(prev => ({ ...prev, profil: e.target.value }))}
              className="champ"
            >
              {PROFILS.map(p => <option key={p.cle} value={p.cle}>{p.libelle}</option>)}
            </select>
          </div>

          {[
            { key: 'first_name', label: 'Prénom', type: 'text' },
            { key: 'last_name', label: 'Nom', type: 'text' },
            // L'adresse est requise : c'est par elle que part le lien de
            // première connexion. Plus de mot de passe à saisir ni à
            // transmettre — l'intervenant choisira le sien.
            { key: 'email', label: 'Adresse e-mail', type: 'email', required: true },
            { key: 'username', label: "Nom d'utilisateur", type: 'text', required: true },
          ].map(f => (
            <div key={f.key}>
              <label className="block surtitre text-[var(--text-muted)] mb-1.5">
                {f.label} {f.required && <span className="text-[var(--alerte)]">*</span>}
              </label>
              <input
                type={f.type}
                required={f.required}
                value={form[f.key]}
                onChange={e => setForm(prev => ({ ...prev, [f.key]: e.target.value }))}
                className="champ"
              />
            </div>
          ))}

          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose}
              className="act act-contour flex-1">
              Annuler
            </button>
            <button type="submit" disabled={loading} aria-busy={loading || undefined}
              className="act flex-1">
              {loading && <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />}
              {loading ? 'Création…' : 'Créer et envoyer le lien'}
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  );
}

export default function Coaches({ user }) {
  const [coaches, setCoaches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [charge, setCharge] = useState(false);       // au moins une réponse reçue
  const chargeRef = useRef(false);
  const [showModal, setShowModal] = useState(false);
  // Deux facettes du meme sujet : ceux qui exercent, et ceux qui postulent.
  // Les separer en deux pages aurait dissocie une decision de son effet.
  // L'onglet suit l'adresse : en revenant d'un dossier de candidature, on
  // retrouve la liste des candidatures et non les coaches en activité.
  const [parametres, setParametres] = useSearchParams();
  const ONGLETS_VALIDES = ['coaches', 'candidatures', 'documents', 'bareme'];
  const onglet = ONGLETS_VALIDES.includes(parametres.get('onglet')) ? parametres.get('onglet') : 'coaches';
  const setOnglet = (cle) => setParametres(cle === 'coaches' ? {} : { onglet: cle }, { replace: true });
  // Vide = tous les profils. Le tri se fait ici et non au serveur : les
  // compteurs des pastilles ont besoin de la liste entiere.
  const [profilFiltre, setProfilFiltre] = useState('');
  const peutDecider = ['SUPER_ADMIN', 'ADMIN'].includes(user?.role);

  const actifs = coaches.filter(c => c.is_active);
  const visibles = profilFiltre
    ? coaches.filter(c => profilDe(c) === profilFiltre)
    : coaches;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await adminApi.getIntervenants();
      setCoaches(Array.isArray(data) ? data : []);
      setError(null);
      chargeRef.current = true;
      setCharge(true);
    } catch (e) {
      // Une actualisation qui échoue garde la liste affichée.
      if (chargeRef.current) annoncerErreur(e.message || 'La liste n’a pas pu être actualisée.');
      else setError(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Le resultat de la derniere action, dit en clair : un compte cree dont le
  // lien n'est pas parti reste inaccessible, et il faut le savoir.
  const [avis, setAvis] = useState(null);
  const [renvoiEnCours, setRenvoiEnCours] = useState(null);
  // L'intervenant dont une action (activer, supprimer) est en cours.
  const [actionEnCours, setActionEnCours] = useState(null);

  const handleCreate = async (form) => {
    const res = await adminApi.createCoach(form);
    load();
    if (res?.email_sent) {
      setAvis({ ton: 'ok', texte: `Compte créé. Le lien de première connexion a été envoyé à ${form.email}.` });
    } else {
      setAvis({ ton: 'alerte', texte: `Compte créé, mais le lien n'a pas pu être envoyé`
        + `${res?.email_error ? ` (${res.email_error})` : ''}. `
        + `Vérifiez l'adresse puis utilisez « Renvoyer le lien ».` });
    }
  };

  const handleRenvoi = async (coach) => {
    setRenvoiEnCours(coach.id);
    try {
      const res = await adminApi.renvoyerLienCoach(coach.id);
      annoncerSucces(`Lien de première connexion renvoyé à ${res?.recipient || coach.email}.`);
    } catch (e) {
      annoncerErreur(e?.message || "L'envoi n'a pas abouti.");
    } finally {
      setRenvoiEnCours(null);
    }
  };

  const handleToggle = async (coach) => {
    setActionEnCours(coach.id);
    try {
      await adminApi.updateCoach(coach.id, { is_active: !coach.is_active });
      annoncerSucces(coach.is_active ? `Compte de ${coach.display_name} désactivé.` : `Compte de ${coach.display_name} activé.`);
      await load();
    } catch (e) {
      annoncerErreur(e?.message || 'Le compte n’a pas pu être modifié.');
    } finally {
      setActionEnCours(null);
    }
  };

  const handleDelete = async (coach) => {
    if (!await confirmer(`Supprimer le compte de ${coach.display_name} ?`)) return;
    setActionEnCours(coach.id);
    try {
      await adminApi.deleteCoach(coach.id);
      annoncerSucces(`Compte de ${coach.display_name} supprimé.`);
      await load();
    } catch (e) {
      annoncerErreur(e?.message || 'Le compte n’a pas pu être supprimé.');
    } finally {
      setActionEnCours(null);
    }
  };

  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
      <div className="flex items-center justify-between mb-8 flex-wrap gap-3">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-pastille bg-[var(--accent)]/15 flex items-center justify-center">
            <UserCog className="w-6 h-6 text-[var(--accent-texte)]" />
          </div>
          <div>
            <h1 className="titre-page">Coaches et mentors</h1>
            <p className="text-sm text-[var(--text-muted)]">
              {loading && !charge ? <Squelette className="h-3 w-48 mt-1" /> : <>
              {actifs.length} compte{actifs.length > 1 ? 's' : ''} en activité
              {coaches.length > actifs.length && ` · ${coaches.length - actifs.length} désactivé${coaches.length - actifs.length > 1 ? 's' : ''}`}
              {actifs.length > 0 && (
                <>
                  {' — '}
                  {PROFILS
                    .map(p => ({ ...p, n: actifs.filter(c => profilDe(c) === p.cle).length }))
                    .filter(p => p.n > 0)
                    .map(p => `${p.n} ${p.libelle.toLowerCase()}${p.n > 1 ? 's' : ''}`)
                    .join(', ')}
                </>
              )}
              </>}
            </p>
          </div>
        </div>
        <button
          onClick={() => setShowModal(true)}
          className="act"
        >
          <Plus className="w-4 h-4" />
          Nouvel intervenant
        </button>
      </div>

      {avis && (
        <div className={`encart encart-${avis.ton} mb-4 flex items-start justify-between gap-3`}>
          <span>{avis.texte}</span>
          <button type="button" onClick={() => setAvis(null)} className="act act-icone act-tertiaire" aria-label="Fermer">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      <div className="flex gap-1 mb-6 border-b border-[var(--border)]">
        {[
          { cle: 'coaches', libelle: 'En activité' },
          { cle: 'candidatures', libelle: 'Candidatures' },
          // Les documents du cycle vivent ici et non sur une page à part :
          // recevoir une fiche, c'est un acte de supervision d'un
          // intervenant, pas une tâche administrative séparée. Les
          // dissocier aurait éloigné la décision de la personne.
          { cle: 'documents', libelle: 'Fiches et relevés' },
          { cle: 'bareme', libelle: 'Barème des grades' },
        ].map((o) => {
          const actif = onglet === o.cle;
          return (
            <button
              key={o.cle}
              onClick={() => setOnglet(o.cle)}
              style={{ minHeight: 44,
                       color: actif ? 'var(--text-bold)' : 'var(--text-muted)',
                       boxShadow: actif ? 'inset 0 -2px 0 var(--accent)' : 'none' }}
              className="act act-tertiaire"
            >
              {o.libelle}
            </button>
          );
        })}
      </div>

      {onglet === 'bareme' ? (
        <BaremeGrades peutModifier={peutDecider} />
      ) : onglet === 'documents' ? (
        <SupervisionDocuments user={user} />
      ) : onglet === 'candidatures' ? (
        <Candidatures />
      ) : loading && !charge ? (
        <div className="bg-[var(--bg-card)] rounded-carte shadow-carte divide-y divide-[var(--border)]">
          {[0, 1, 2].map(i => (
            <div key={i} className="flex items-center gap-4 px-5 py-3.5">
              <Squelette className="w-10 h-10 rounded-pastille" />
              <div className="flex-1 space-y-2"><Squelette className="h-3.5 w-40" /><Squelette className="h-3 w-64" /></div>
              <Squelette className="h-6 w-16" />
            </div>
          ))}
        </div>
      ) : error && !charge ? (
        <div className="rounded-carte bg-[var(--bg-card)] shadow-carte"><EtatErreur erreur={error} onReessayer={load} /></div>
      ) : coaches.length === 0 ? (
        <div className="rounded-carte bg-[var(--bg-card)] shadow-carte">
          <EtatVide
            icone={UserCog}
            titre="Aucun coach ni mentor"
            aide="Créez un compte pour qu'un intervenant puisse accompagner une organisation."
            action={(
              <button type="button" onClick={() => setShowModal(true)} className="act">
                Créer le premier compte
              </button>
            )}
          />
        </div>
      ) : (
        <>
          {/* Les pastilles ne montrent que les profils reellement presents :
              proposer « Bénévole » quand il n'y en a aucun fait cliquer pour
              rien et donne a la barre une longueur qu'elle ne merite pas. */}
          <div className="flex flex-wrap gap-2 mb-5">
            {[{ cle: '', libelle: 'Tous', n: coaches.length }]
              .concat(PROFILS
                .map(p => ({ ...p, n: coaches.filter(c => profilDe(c) === p.cle).length }))
                .filter(p => p.n > 0))
              .map(p => {
                const actif = profilFiltre === p.cle;
                return (
                  <button
                    key={p.cle || 'tous'}
                    type="button"
                    onClick={() => setProfilFiltre(p.cle)}
                    
                    className={`px-3.5 rounded-controle text-[12.5px] font-semibold border transition-colors ${
                      actif
                        ? 'bg-[var(--accent)] text-[#0A1128] border-transparent'
                        : 'bg-[var(--bg-card)] text-[var(--text-muted)] border-[var(--border)] hover:text-[var(--text-bold)]'
                    }`}
                  >
                    {p.libelle} <span className="opacity-60">{p.n}</span>
                  </button>
                );
              })}
          </div>

        {visibles.length === 0 ? (
          <div className="rounded-carte bg-[var(--bg-card)] shadow-carte">
            <EtatSansDonnees cas="sansFiltre" titre="Aucun intervenant avec ce profil"
              action={<button type="button" className="act act-sm act-contour" onClick={() => setProfilFiltre('')}>Réinitialiser</button>} />
          </div>
        ) : (
        /* Une ligne par personne, superposées : on parcourt une équipe comme
           une liste, et les cartes en grille obligeaient à balayer l'écran
           en zigzag pour comparer deux intervenants. */
        <ul className="bg-[var(--bg-card)] rounded-carte shadow-carte divide-y divide-[var(--border)]">
          {visibles.map(coach => {
            const statut = !coach.is_active ? 'Désactivé' : coach.first_login ? 'Lien envoyé, compte non activé' : 'Actif';
            return (
            <motion.li
              key={coach.id}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className={`flex items-center gap-4 px-5 py-3.5 flex-wrap sm:flex-nowrap ${coach.is_active ? '' : 'opacity-70'}`}
            >
              <span className="w-10 h-10 shrink-0 rounded-pastille bg-[var(--accent)]/15 grid place-items-center">
                <UserCog className="w-5 h-5 text-[var(--accent-texte)]" />
              </span>

              {/* Sur téléphone, le nom prend toute la ligne : statut et boutons
                  passent dessous au lieu de réduire le nom à trois lettres. */}
              <div className="min-w-0 flex-1 basis-[calc(100%-3.5rem)] sm:basis-auto">
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className="font-semibold text-[var(--text-bold)] truncate">{coach.display_name}</h3>
                  <span className="px-2 py-0.5 rounded-controle text-[11px] font-semibold
                                   bg-[var(--accent)]/12 text-[var(--accent-texte)] border border-[var(--accent)]/25">
                    {coach.profil_display}
                  </span>
                </div>
                <p className="text-xs text-[var(--text-muted)] flex items-center gap-x-3 gap-y-0.5 flex-wrap mt-0.5">
                  <span className="font-semibold">@{coach.username}</span>
                  {coach.email && (
                    <span className="inline-flex items-center gap-1 min-w-0">
                      <Mail className="w-3.5 h-3.5 shrink-0" />
                      <span className="truncate">{coach.email}</span>
                    </span>
                  )}
                </p>
              </div>

              <span className={`shrink-0 inline-flex px-2.5 py-1 ml-14 sm:ml-0 rounded-controle text-[12px] font-semibold ${
                !coach.is_active || coach.first_login ? 'bg-[var(--bg-main)] text-[var(--text-muted)]'
                  : 'bg-[var(--vert-pale)] text-[var(--accent-texte)]'
              }`}>
                {statut}
              </span>

              <div className="flex items-center gap-1 shrink-0 ml-auto sm:ml-0">
                {coach.first_login && peutDecider && (
                  <button type="button" onClick={() => handleRenvoi(coach)}
                    disabled={renvoiEnCours === coach.id} aria-busy={renvoiEnCours === coach.id || undefined}
                    className="act act-sm act-contour">
                    {renvoiEnCours === coach.id
                      ? <Loader2 className="w-3.5 h-3.5 animate-spin" aria-hidden="true" />
                      : <Send className="w-3.5 h-3.5" />}
                    {renvoiEnCours === coach.id ? 'Envoi…' : 'Renvoyer le lien'}
                  </button>
                )}
                <button type="button" onClick={() => handleToggle(coach)}
                  disabled={actionEnCours === coach.id} aria-busy={actionEnCours === coach.id || undefined}
                  className="act act-icone act-tertiaire"
                  title={coach.is_active ? 'Désactiver' : 'Activer'}
                  aria-label={`${coach.is_active ? 'Désactiver' : 'Activer'} ${coach.display_name}`}
                >
                  {coach.is_active
                    ? <ToggleRight className="w-5 h-5 text-[var(--accent-texte)]" />
                    : <ToggleLeft className="w-5 h-5 text-[var(--text-muted)]" />
                  }
                </button>
                <button type="button" onClick={() => handleDelete(coach)}
                  disabled={actionEnCours === coach.id}
                  title="Supprimer" aria-label={`Supprimer ${coach.display_name}`}
                  className="act act-icone act-danger">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </motion.li>
            );
          })}
        </ul>
        )}
        </>
      )}

      {showModal && <CoachModal onClose={() => setShowModal(false)} onSave={handleCreate} />}
    </motion.div>
  );
}
